<template>
  <template v-if="checklistPending">
    <v-skeleton-loader type="table-heading, table-tbody"/>
  </template>
  <template v-else-if="showEmptyState">
    <app-empty-state 
      class="mt-16"
      module="checklist"
      :translation-key-prefix="TRANSLATION_KEY_PREFIX"
    />
  </template>
  <template v-else>
    <AppCard>
      <div
        class="checklist-table"
        :style="{
          '--column-title-min-width': computedTitleWidth,
        }"
        data-locator="checklist-table"
        ref="checklistTable"
        @scroll="syncScroll"
        v-resize="onTableResize"
      >
        <ChecklistTableHeader ref="checklistTableHeader" @mouseenter="startAutoScroll('up')" @mouseleave="stopAutoScroll"/>

        <template v-if="filteredChecklist.length === 0">
          <div class="checklist-table-no-result pa-6 pb-2">
            <template v-if="isChecklistFiltered">
              <DataTableFilterNoData />
            </template>
            <template v-else-if="flatChecklist.length === 0">
              <i18n-t :keypath="displayedNoTasksText" tag="p" scope="global" class="text-grey-500">
                <template #highlight>
                  <span class="text--bold">{{ $t(`${TRANSLATION_KEY_PREFIX}.noTasksHighlight`) }}</span>
                </template>
              </i18n-t>
            </template>
          </div>
        </template>

        <ChecklistTableBody :checklist-model="checklistModel" />
        <template v-if="isCurrentUserPm">
          <div class="checklist-table-add-button" @mouseenter="startAutoScroll('down')" @mouseleave="stopAutoScroll">
            <div class="checklist-table-add-button-inner">
              <ChecklistTableAddButton 
                data-locator="add-element-menu-button" 
                :mnemo="roomMnemo"
                :disable-task-creation="isLastSectionAwaitingDelete"
                type="ghost"
              />
            </div>
          </div>
        </template>
      </div>
    </AppCard>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import DataTableFilterNoData from '@/common/filters/DataTableFilterNoData.vue'
import { toast } from '@/common/utils/toast'
import { surveyCanBeTriggered, SurveyId, triggerSurvey } from '@/plugins/satismeter'
import { COLUMN_TITLE_MIN_WIDTH } from '@/project/checklist/enums'
import ChecklistTableAddButton from '@/project/checklist/table/ChecklistTableAddButton.vue'
import ChecklistTableBody from '@/project/checklist/table/ChecklistTableBody.vue'
import ChecklistTableHeader from '@/project/checklist/table/ChecklistTableHeader.vue'
import { ChecklistSection } from '@/project/checklist/types'
import {
  GET_CHECKLIST,
  GET_CHECKLIST_LAST_UPDATES,
  GET_TASK_STATUS_CATEGORIES,
} from '@/store/modules/checklist/action_types'
import {
  RESET_CHECKLIST,
  SET_CHECKLIST_STRUCTURE,
} from '@/store/modules/checklist/mutation_types'

const REFRESH_LOOP_POLLING_DELAY = 30000
const TRANSLATION_KEY_PREFIX = 'project.checklist.ChecklistTable'

type DataType = {
  TRANSLATION_KEY_PREFIX: string,
  firstTaskHasBeenCreated: boolean,
  isTableHorizontallyScrolled: boolean,
  refreshLoopId: NodeJS.Timeout | number,
  tableComputedWidth: number,
  autoScrollInterval: number | null,
}

export default defineComponent({
  name: 'ChecklistTable',
  components: {
    AppCard,
    ChecklistTableAddButton,
    ChecklistTableBody,
    ChecklistTableHeader,
    DataTableFilterNoData,
  },
  data (): DataType {
    return {
      TRANSLATION_KEY_PREFIX,
      firstTaskHasBeenCreated: false,
      isTableHorizontallyScrolled: false,
      refreshLoopId: 0,
      tableComputedWidth: 0,
      autoScrollInterval: null,
    }
  },
  provide () {
    return {
      checklistTableWidth: computed(() => this.tableComputedWidth),
      isTableHorizontallyScrolled: computed(() => this.isTableHorizontallyScrolled),
    }
  },
  computed: {
    ...mapState('checklist', [
      'checklistPending',
      'checklistTableHeader',
      'checklistItemsBeingEdited',
      'checklistDragItem',
    ]),
    ...mapState('user', ['profile']),
    ...mapGetters('checklist', [
      'checklist',
      'filteredChecklist',
      'flatChecklist',
      'isChecklistFiltered',
      'isPristineChecklist',
    ]),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    isLastSectionAwaitingDelete (): boolean {
      const lastSection = this.flatChecklist.findLast((item: any) => item.type === 'title')
      return lastSection?.isAwaitingValidation === true
    },
    checklistModel: {
      get () {
        return this.filteredChecklist
      },
      set (value: Array<ChecklistSection>) {
        this.SET_CHECKLIST_STRUCTURE(value)
      },
    },
    displayedNoTasksText() {
      return `${TRANSLATION_KEY_PREFIX}.${this.isCurrentUserPm ? 'noTasksText' : 'noTasksTextGuests'}`
    },
    rowWidth () {
      return `${this.tableComputedWidth}px`
    },
    showEmptyState () {
      return this.isPristineChecklist && this.filteredChecklist.length === 0
    },
    tableUsersColumnsCount () {
      return this.checklistTableHeader.length
    },
    computedTitleWidth () {
      return COLUMN_TITLE_MIN_WIDTH.FULL
    },
  },
  async mounted () {
    this.GET_GROUPS(this.roomMnemo)
    this.prepareGetTaskStatuses()

    try {
      await this.GET_CHECKLIST(this.roomMnemo)
    } catch (e) {
      console.error(e)
    }

    this.refreshLoopId = setTimeout(this.refreshLoop, REFRESH_LOOP_POLLING_DELAY)
  },
  beforeUnmount () {
    this.RESET_CHECKLIST()
    clearTimeout(this.refreshLoopId)
    // Set to a recognizable value so that we know if we should stop refreshing if already fired
    this.refreshLoopId = -1

    const { id: userId, fullName, email } = this.profile
    if (this.firstTaskHasBeenCreated && surveyCanBeTriggered(SurveyId.CSAT, userId)) {
      triggerSurvey(SurveyId.CSAT, userId, fullName, email, true)
    }
  },
  methods: {
    ...mapActions('checklist', [
      GET_CHECKLIST,
      GET_CHECKLIST_LAST_UPDATES,
      GET_TASK_STATUS_CATEGORIES,
    ]),
    ...mapActions('groups', ['GET_GROUPS']),
    ...mapMutations('checklist', [
      RESET_CHECKLIST,
      SET_CHECKLIST_STRUCTURE,
    ]),
    onTableResize () {
      this.tableComputedWidth = (this.$refs['checklistTable'] as HTMLElement).clientWidth
    },
    async prepareGetTaskStatuses () {
      try {
        await this.GET_TASK_STATUS_CATEGORIES({
          mnemo: this.roomMnemo,
        })
      } catch (e) {
        console.error(e)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.getTaskStatusesError`))
      }
    },
    async refreshLoop () {
      const waitAndRefresh = () => {
        if (this.refreshLoopId !== -1) {
          this.refreshLoopId = setTimeout(this.refreshLoop, REFRESH_LOOP_POLLING_DELAY)
        }
      }

      if (this.checklistItemsBeingEdited.length > 0) {
        waitAndRefresh()
      } else {
        try {
          await this.GET_CHECKLIST_LAST_UPDATES({
            mnemo: this.roomMnemo,
          })
        } finally {
          waitAndRefresh()
        }
      }
    },
    syncScroll ($event: Event) {
      if ($event.target) {
        this.isTableHorizontallyScrolled = ($event.target as HTMLDivElement).scrollLeft > 0
      }
    },
    startAutoScroll(direction: 'up' | 'down') {
      if (!this.checklistDragItem) return  

      const checklistTableEl = this.$refs['checklistTable'] as HTMLElement
      if (!checklistTableEl || this.autoScrollInterval) return

      const autoScrollStep = direction === 'down' ? 40 : -40
      this.autoScrollInterval = window.setInterval(() => {
        checklistTableEl.scrollBy({
          top: autoScrollStep,
          behavior: 'smooth',
        })
      }, 50)
    },
    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval)
        this.autoScrollInterval = null
      }
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/project/checklist/table/checklist-table.scss";

.checklist-table-body {
  background-color: inherit;
  display: grid;
  grid-template-columns: subgrid;
  grid-column: row-start / row-end;
}

.checklist-table {
  display: grid;
  grid-template-columns:
    [row-start]
    var(--column-select-width)
    minmax(var(--column-title-min-width), 0.6fr)
    minmax(var(--column-comment-width), 0.4fr)
    repeat(v-bind(tableUsersColumnsCount -1 ), var(--column-custom-width))
    var(--column-action-width)
    [row-end];
  overflow-x: auto;
  background-color: rgb(var(--v-theme-white));
  line-height: normal;

  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 100px;
    background: rgb(var(--v-theme-grey-50))
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background: rgb(var(--v-theme-grey-200));
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgb(var(--v-theme-grey-300));
  }
}

.checklist-table-no-result {
  width: v-bind(rowWidth);
  position: sticky;
  left: 0;
  grid-column: row-start / row-end;
}

.checklist-table-add-button {
  grid-column: row-start / row-end;
  position: sticky;
  bottom: 0;
  background-color: rgb(var(--v-theme-white));
  z-index: 1;
}

.checklist-table-add-button-inner {
  width: v-bind(rowWidth);
  position: sticky;
  left: 0;
  padding: 8px 28px;
}
</style>
